import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    DataSources,
    EmployeesClient, PermissionType,
    ProfileCustomerDto,
    UpdateProfileCustomerCommand
} from '@services';
import {
    DxTagBoxModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule
} from 'devextreme-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import notify from 'devextreme/ui/notify';

@Component({
    selector: 'app-customer-details',
    templateUrl: './customer-details.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxTagBoxModule,
        DxValidationGroupModule,
        DxValidatorModule
    ]
})
export class CustomerDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    private dataSources = inject(DataSources);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();

    @ViewChild('validationGroup', { static: false }) validationGroup: DxValidationGroupComponent;

    profileDetails: ProfileCustomerDto;
    isLoading = false;

    protected customerDataSource = this.dataSources.customerOptions();

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getProfileCustomer(this.userId).subscribe({
            next: (data) => {
                this.profileDetails = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load customer details', 'error', 3000);
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly) return of(void 0);
        if (!this.validationGroup) return of();

        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({ message: 'Please fix customer details errors', position: 'bottom center' }, 'error', 3000);
            return throwError(() => new Error('Validation failed'));
        }

        const command = new UpdateProfileCustomerCommand({
            userId: this.userId,
            customers: this.profileDetails.customers
        });

        return this.employeesClient.updateProfileCustomer(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update customer details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserCustomerDetailsOwn, PermissionType.UserCustomerDetailsOthers);
    }
}
